<!--This is the Error Card that appears on the home dashboard for a particular plan holder-->
<template>
	<div
		v-if="errorCount > 0"
		class="flex cursor-pointer items-center rounded-lg bg-red-100 px-3 py-2"
		:class="{ 'w-full md:w-1/2': hasOnlyOneError }"
		@click="emit('openSubmissionStatus', planHolderId)"
	>
		<div class="min-w-0 flex-1 truncate text-sm font-bold text-red-700">
			{{ planHolder?.lastName }}, {{ planHolder?.firstName }}
		</div>

		<div
			class="shrink-0 rounded-full bg-red-400 px-3 py-1 text-xs font-semibold text-white"
		>
			{{ errorCount }}
			{{ errorCount === 1 ? 'error' : 'errors' }}
		</div>

		<Menu
			as="div"
			class="relative ml-2 inline-block text-left"
			@click.stop
		>
			<MenuButton class="flex items-center">
				<EllipsisVerticalIcon class="size-5" />
			</MenuButton>
			<transition
				enter-active-class="transition duration-100 ease-out"
				enter-from-class="transform scale-95 opacity-0"
				enter-to-class="transform scale-100 opacity-100"
				leave-active-class="transition duration-75 ease-in"
				leave-from-class="transform scale-100 opacity-100"
				leave-to-class="transform scale-95 opacity-0"
			>
				<MenuItems
					@click.prevent=""
					as="section"
					class="text-secondary-800 absolute right-0 z-20 flex w-72 flex-col rounded-lg border border-slate-100 bg-white font-light shadow-md"
				>
					<MenuItem
						as="component"
						class="rounded-t-lg p-3 pb-2 hover:cursor-pointer hover:bg-slate-100"
						@click.prevent="downloadFinalDocument"
					>
						{{ t('document.submission.downloadDocuments') }}
					</MenuItem>
					<!-- TODO: PSS-1916 Add manual resolve button back in once PSS-1916 ticket is done" -->
					<!-- <MenuItem
						as="component"
						class="rounded-b-lg p-3 pt-2 hover:cursor-pointer hover:bg-slate-100"
						@click.prevent="markAsManuallyResolved"
					>
						{{ t('document.submission.manuallyResolved') }}
					</MenuItem> -->
				</MenuItems>
			</transition>
		</Menu>

		<!-- Hidden container required for Foxit to initialize the PDF viewer used to merge documents -->
		<div
			id="print-error-submission"
			class="hidden"
		></div>
	</div>
</template>

<script setup lang="ts">
import type { Session } from '@/classes/Session'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { useFoxitManager } from '@/components/documents/Foxit/composables/FoxitManager'
import { getPolicyPacketDocuments } from '@/components/home/composables/getPolicyPacketDocuments'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { usePresentationLocale } from '@/i18n'
import { type PlanVersion, usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import { usePlanStore } from '@/stores/plan'
import { useSubmissionStore } from '@/stores/submission'
import type { PlanBasicData } from '@/types'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid'
import { captureException } from '@sentry/vue'
import { onMounted, type PropType, ref } from 'vue'
import { useRouter } from 'vue-router'

const submissionStore = useSubmissionStore()
const planStore = usePlanStore()
const { t } = usePresentationLocale()
const planHolder = ref<Partial<PlanBasicData> | undefined>(undefined)

const { prepareUiandViewer, getPdfViewer } = useFoxitInstance()
const foxitManager = useFoxitManager()
const router = useRouter()
const { gatherDocumentsForAppointment } = getPolicyPacketDocuments()

const versionHelper = usePlanVersionsV2()
const latestPlanVersionByPerson = ref<Record<string, PlanVersion>>({})

const props = defineProps({
	planHolderId: {
		type: String,
		required: true,
	},
	session: {
		type: Object as PropType<Session>,
		required: false,
	},
	errorCount: {
		type: Number,
		required: true,
	},
	hasOnlyOneError: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits<{
	openSubmissionStatus: [planHolderId: string]
}>()

onMounted(async () => {
	// The plan may only exist in CloudSync if it was submitted from another device
	planHolder.value = await submissionStore.loadPlanHolderBasicData(
		props.planHolderId
	)

	latestPlanVersionByPerson.value =
		await versionHelper.getLatestPlanVersionsFromAppointment(props.session)
})

/** This function will download the final document*/
const downloadFinalDocument = async () => {
	try {
		const plans = Object.values(latestPlanVersionByPerson.value)
		const documentGroups = await gatherDocumentsForAppointment(plans)
		const documents = documentGroups.flat()

		if (!documents.length) {
			console.warn('[SubmissionErrorCard] No documents found to download')
			return
		}

		// Prepare PDF viewer which is required for merging documents
		if (!getPdfViewer()) {
			await prepareUiandViewer('print-error-submission')
		}

		const buffer = await foxitManager.mergeDocs(documents)

		if (!buffer) return

		const file = new Blob([buffer], { type: 'application/pdf' })
		await printPdfPreview(file)
	} catch (e) {
		captureException(e, {
			extra: {
				message: 'Failed to Download Packet',
			},
		})
	}
}

// Open the PDF in a new window for preview
const printPdfPreview = async (file: Blob) => {
	const url = URL.createObjectURL(file)

	const routeData = router.resolve({
		path: '/print',
		query: { file: url },
	})

	window.open(routeData.href, '_blank')
}

/** This function will set the PlanHolder's packetStatus to ManuallyResolved and prune the request from the retry queue*/
const markAsManuallyResolved = async () => {
	try {
		await planStore.updatePlan(props.planHolderId, {
			packetStatus: PlanPacketStatus.ManuallyResolved,
		})
	} catch (e) {
		// The plan is not in localforage, so only its submission errors can be dismissed
		// TODO: Either hide the Manually Resolve option if there are only remote errors,
		// or find a way to make it ignore those remote errors?
	}

	await submissionStore.removeSubmissionByPlanHolderId(props.planHolderId)
}
</script>
